import styled from 'styled-components/macro'
import { darken } from 'polished'

import { RowBetween } from '../Row'
import { ChevronDown, Check } from 'react-feather'
import { Button as RebassButton, ButtonProps as ButtonPropsOriginal } from 'rebass/styled-components'
import useTheme from 'hooks/useTheme'

type ButtonProps = Omit<ButtonPropsOriginal, 'css'>

const Base = styled(RebassButton)<
  {
    padding?: string
    width?: string
    $borderRadius?: string
    altDisabledStyle?: boolean
  } & ButtonProps
>`
  padding: ${({ padding }) => padding ?? '16px'};
  width: ${({ width }) => width ?? '100%'};
  font-weight: 500;
  text-align: center;
  border-radius: ${({ $borderRadius }) => $borderRadius ?? '8px'};
  outline: none;
  border: 1px solid transparent;
  text-shadow: 0px 0px 4px black;
  color: white;
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  &:disabled {
    cursor: auto;
    pointer-events: none;
  }

  will-change: transform;
  transition: transform 450ms ease;
  transform: perspective(1px) translateZ(0);

  > * {
    user-select: none;
  }

  > a {
    text-decoration: none;
  }
`

export const ButtonPrimary = styled(Base)`
  background-color: #d88867 !important;
  font-size: 20px;
  font-weight: 600;
  padding: 16px;
  text-shadow: 0px 0px 4px black;
  color: white;
  &:focus {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:hover {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:active {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:disabled {
    background-color: #191b1f77 !important;
    color: ${({ theme }) => theme.text2};
    cursor: auto;
    box-shadow: none;
    border: 1px solid transparent;
    outline: none;
  }
`

export const ButtonLight = styled(Base)`
  background-color: ${({ theme }) => theme.primary5};
  color: ${({ theme }) => theme.primaryText1};
  font-size: 16px;
  text-shadow: 0px 0px 4px black;
  font-weight: 500;
  &:focus {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:hover {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:active {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  :disabled {
    opacity: 0.4;
    :hover {
      cursor: auto;
      background-color: ${({ theme }) => theme.primary5};
      box-shadow: none;
      border: 1px solid transparent;
      outline: none;
    }
  }
`

export const ButtonGray = styled(Base)`
  background-color: #d88867;
  color: white;
  text-shadow: 0px 0px 4px black;
  font-size: 16px;
  font-weight: 500;

  &:hover {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:active {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
`

export const ButtonSecondary = styled(Base)`
  background: linear-gradient(90deg, #473933ee 0%, #654b41ee 50%, #473933ee 100%);
  color: white;
  text-shadow: 0px 0px 4px black;
  border: 1px solid rgba(251, 231, 198, 0.2);
  box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  border-radius: 10px;
  font-size: 16px;
  padding: ${({ padding }) => (padding ? padding : '10px')};

  &:focus {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:hover {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:active {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
  a:hover {
    text-decoration: none;
  }
`

export const ButtonOutlined = styled(Base)`
  border: 1px solid ${({ theme }) => theme.bg2};
  background-color: transparent;
  text-shadow: 0px 0px 4px black;
  color: ${({ theme }) => theme.text1};
  &:focus {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:hover {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:active {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonYellow = styled(Base)`
  background-color: ${({ theme }) => theme.yellow3};
  color: white;
  text-shadow: 0px 0px 4px black;
  &:focus {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:hover {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:active {
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:disabled {
    background-color: ${({ theme }) => theme.yellow3};
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonEmpty = styled(Base)`
  background-color: transparent;
  color: ${({ theme }) => theme.primary1};
  display: flex;
  text-shadow: 0px 0px 4px black;
  justify-content: center;
  align-items: center;

  &:focus {
    text-decoration: underline;
  }
  &:hover {
    text-decoration: none;
  }
  &:active {
    text-decoration: none;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonText = styled(Base)`
  padding: 0;
  width: fit-content;
  text-shadow: 0px 0px 4px black;
  background: none;
  text-decoration: none;
  &:focus {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    text-decoration: underline;
  }
  &:hover {
    // text-decoration: underline;
    opacity: 0.9;
  }
  &:active {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

const ButtonConfirmedStyle = styled(Base)`
  background-color: ${({ theme }) => theme.bg3};
  text-shadow: 0px 0px 4px black;
  color: ${({ theme }) => theme.text1};
  /* border: 1px solid ${({ theme }) => theme.green1}; */

  &:disabled {
    /* opacity: 50%; */
    background-color: ${({ theme }) => theme.bg2};
    color: ${({ theme }) => theme.text2};
    cursor: auto;
  }
`

const ButtonErrorStyle = styled(Base)`
  padding: 16px;
  width: 100%;
  text-align: center;
  border-radius: 8px;
  outline: none;
  text-decoration: none;
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  flex-wrap: nowrap;
  -webkit-box-align: center;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  will-change: transform;
  text-shadow: 0px 0px 4px black;
  transition: transform 450ms ease 0s;
  transform: perspective(1px) translateZ(0px);
  background: #54565c;
  color: white;
  font-size: 16px;
  font-weight: 500;
  transition: 0.2s ease-in-out;

  &:focus {
    transition: 0.2s ease-in-out;
    box-shadow: 0 0 0 1pt darken(0.01, theme.primary5))};
    background: #1d1e20;
  }
  &:hover {
    transition: 0.2s ease-in-out;
    transform: scale(0.99);
    box-shadow: rgba(251, 231, 198, 0.8) 0px 0px 5px, rgba(251, 231, 198, 0.8) 0px 0px 7px;
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.primary5)};
    background-color: ${({ theme }) => darken(0.1, theme.primary5)};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
    box-shadow: none;
    background-color: ${({ theme }) => theme.red1};
    border: 1px solid ${({ theme }) => theme.red1};
  }
`

export function ButtonConfirmed({
  confirmed,
  altDisabledStyle,
  ...rest
}: { confirmed?: boolean; altDisabledStyle?: boolean } & ButtonProps) {
  if (confirmed) {
    return <ButtonConfirmedStyle {...rest} />
  } else {
    return <ButtonPrimary {...rest} altDisabledStyle={altDisabledStyle} />
  }
}

export function ButtonError({ error, ...rest }: { error?: boolean } & ButtonProps) {
  if (error) {
    return <ButtonErrorStyle {...rest} />
  } else {
    return <ButtonPrimary {...rest} />
  }
}

export function ButtonDropdown({ disabled = false, children, ...rest }: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonPrimary {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonPrimary>
  )
}

export function ButtonDropdownLight({ disabled = false, children, ...rest }: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonOutlined {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonOutlined>
  )
}

const ActiveOutlined = styled(ButtonOutlined)`
  border: 1px solid;
  border-color: ${({ theme }) => theme.primary1};
`

const Circle = styled.div`
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.primary1};
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckboxWrapper = styled.div`
  width: 30px;
  padding: 0 10px;
  position: absolute;
  top: 10px;
  right: 10px;
`

const ResponsiveCheck = styled(Check)`
  size: 13px;
`

export function ButtonRadioChecked({ active = false, children, ...rest }: { active?: boolean } & ButtonProps) {
  const theme = useTheme()

  if (!active) {
    return (
      <ButtonOutlined $borderRadius="12px" padding="12px 8px" {...rest}>
        {<RowBetween>{children}</RowBetween>}
      </ButtonOutlined>
    )
  } else {
    return (
      <ActiveOutlined {...rest} padding="12px 8px" $borderRadius="12px">
        {
          <RowBetween>
            {children}
            <CheckboxWrapper>
              <Circle>
                <ResponsiveCheck size={13} stroke={theme.white} />
              </Circle>
            </CheckboxWrapper>
          </RowBetween>
        }
      </ActiveOutlined>
    )
  }
}
